import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { Package } from '@frontend/package/types';
import { LuCheck, LuChevronUp } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import LargeSlotCard from '../../../components/slot-cards/large-slot-card.component';
import SmallSlotCard from '../../../components/slot-cards/small-slot-card.component';
import useProcessOpenCloseSlots from './process-open-close-slots.controller';

const ID = 'process-open-close-slots';

const SLOT_THRESHOLD = 6;

export interface ProcessOpenCloseSlotsProps {
    onContinue?: () => void;
    packages: Package[];
    isLoadingCallback?: (isLoading: boolean) => void;
    allClosedCallback?: (allClosed: boolean) => void;
}

const ProcessOpenCloseSlots = (props: ProcessOpenCloseSlotsProps) => {
    const viewProps = useProcessOpenCloseSlots(props);

    return (
        <div
            id={ID}
            className='flex size-full flex-col gap-6 overflow-x-hidden overflow-y-auto p-6'>
            {viewProps.slots && viewProps.slotStatusMap ? (
                <>
                    {viewProps.allSlotsClosed ? (
                        <div className='bg-background-surface border-border-default inline-flex flex-col items-center justify-start gap-4 self-stretch rounded-xl border p-4'>
                            <div className='bg-status-success flex size-9 items-center justify-center rounded-full'>
                                <LuCheck className='text-background-surface size-6' />
                            </div>

                            <div className='flex flex-col items-center justify-start gap-1 self-stretch'>
                                <div className='text-foreground-default justify-start text-base font-semibold'>
                                    <FormattedMessage
                                        defaultMessage='All SLOTs are now closed'
                                        id='Wizard.ProcessOpenCloseSlots.AllSlotsClosed'
                                        description='Message displayed to inform the user that all SLOTs are now closed.'
                                    />
                                </div>
                                <div className='text-brand-muted justify-start self-stretch text-center text-sm font-medium'>
                                    <FormattedMessage
                                        defaultMessage='Thank you!'
                                        id='Wizard.ProcessOpenCloseSlot.ExitInstruction'
                                        description='Instruction to the user that they can exit now after closing the slot.'
                                    />
                                </div>
                            </div>

                            {props.onContinue && (
                                <button
                                    type='button'
                                    className='bg-brand-primary text-background-surface flex items-center justify-center rounded-xl px-4 py-3 text-base font-medium'
                                    onClick={props.onContinue}>
                                    {CommonMessage.BUTTONS.CONTINUE}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className='inline-flex flex-col items-start justify-start gap-3 self-stretch'>
                            <div className='text-foreground-default w-full text-base font-medium'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.open-slots'
                                    defaultMessage='Open SLOTs'
                                />
                            </div>
                            {viewProps.slots.length <= SLOT_THRESHOLD ? (
                                <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                                    {viewProps.openSlots.map((slot) => (
                                        <LargeSlotCard
                                            key={slot.id}
                                            slot={slot}
                                            status={viewProps.slotStatusMap?.get(slot.id)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className='grid grid-cols-2 gap-2 self-stretch'>
                                    {viewProps.openSlots.map((slot) => (
                                        <SmallSlotCard
                                            key={slot.id}
                                            slot={slot}
                                            status={viewProps.slotStatusMap?.get(slot.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className='inline-flex flex-col items-start justify-start gap-3 self-stretch'>
                        <div
                            className='inline-flex items-start justify-between self-stretch'
                            onClick={viewProps.toggleShowClosedSlots}>
                            <div className='text-foreground-default justify-center text-base font-medium'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.closed-slots'
                                    defaultMessage='Closed SLOTs'
                                />
                            </div>
                            <LuChevronUp className={`text-foreground-default size-5 transition-transform ${viewProps.showClosedSlots ? '' : 'rotate-180'}`} />
                        </div>
                        {viewProps.showClosedSlots &&
                            (viewProps.slots.length <= SLOT_THRESHOLD ? (
                                <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                                    {viewProps.closedSlots.map((slot) => (
                                        <LargeSlotCard
                                            key={slot.id}
                                            slot={slot}
                                            status={viewProps.slotStatusMap?.get(slot.id)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className='grid grid-cols-2 gap-2 self-stretch transition-all'>
                                    {viewProps.closedSlots.map((slot) => (
                                        <SmallSlotCard
                                            key={slot.id}
                                            slot={slot}
                                            status={viewProps.slotStatusMap?.get(slot.id)}
                                        />
                                    ))}
                                </div>
                            ))}
                    </div>
                </>
            ) : (
                <Spinner />
            )}
        </div>
    );
};

export default ProcessOpenCloseSlots;
